import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import {
  AdminTable,
  type AdminTableColumn,
  FilterToggle,
  Pagination,
  PageHeader,
  SearchBar,
  StatusPill,
  type PillTone,
  useDebouncedValue,
} from "../../components/admin/adminPrimitives";
import {
  pgIdList,
  useInternalAccountIds,
} from "../../components/admin/isAdmin";

interface License {
  id: string;
  license_key: string;
  status: string;
  label: string | null;
  trial_ends_at: string | null;
  purchased_at: string | null;
  support_ends_at: string | null;
  created_at: string;
  school_name: string;
  account_id: string;
  activations_count: number;
}

const PAGE_SIZE = 25;

const statusTones: Record<string, PillTone> = {
  "in-use": "forest",
  available: "sky",
  active: "forest",
  trial: "gold",
  expired: "bark",
  revoked: "red",
};

export function Licenses() {
  const [licenses, setLicenses] = useState<License[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [showInternal, setShowInternal] = useState(false);
  const internalAccountIds = useInternalAccountIds();

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, showInactive, showInternal]);

  useEffect(() => {
    // Wait for the internal-account lookup before firing the main query,
    // otherwise the first fetch runs without the filter and flashes
    // internal rows into view.
    if (!showInternal && internalAccountIds === null) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);

      const from = page * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let query = supabase
        .from("licenses")
        .select(
          `id, license_key, status, label, trial_ends_at, purchased_at,
           support_ends_at, created_at, account_id,
           accounts:account_id ( school_name ),
           activations(id)`,
          { count: "exact" },
        )
        .order("created_at", { ascending: false })
        .range(from, to);

      if (!showInactive) {
        // "available" and "in-use" are both derived from status=active,
        // so hiding revoked + expired means we only want active + trial.
        query = query.in("status", ["active", "trial"]);
      }

      if (!showInternal && internalAccountIds && internalAccountIds.length > 0) {
        query = query.not("account_id", "in", pgIdList(internalAccountIds));
      }

      const q = debouncedSearch.trim();
      if (q) {
        const safe = q.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
        query = query.or(`license_key.ilike.%${safe}%,label.ilike.%${safe}%`);
      }

      const { data, count, error: queryError } = await query;
      if (cancelled) return;

      if (queryError) {
        console.error("Licenses fetch error:", queryError);
        setError("Failed to load licences: " + queryError.message);
        setLicenses([]);
        setTotal(0);
        setLoading(false);
        return;
      }

      setLicenses(
        (data ?? []).map((l: Record<string, unknown>) => ({
          id: l.id as string,
          license_key: l.license_key as string,
          status: l.status as string,
          label: (l.label as string | null) ?? null,
          trial_ends_at: (l.trial_ends_at as string | null) ?? null,
          purchased_at: (l.purchased_at as string | null) ?? null,
          support_ends_at: (l.support_ends_at as string | null) ?? null,
          created_at: l.created_at as string,
          account_id: l.account_id as string,
          school_name:
            ((l.accounts as { school_name: string } | null)?.school_name ??
              "Unknown"),
          activations_count:
            ((l.activations as { id: string }[] | null) ?? []).length,
        })),
      );
      setTotal(count ?? 0);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [page, debouncedSearch, showInactive, showInternal, internalAccountIds]);

  const columns: AdminTableColumn<License>[] = [
    {
      key: "school",
      header: "School",
      render: (l) => (
        <Link
          to={`/admin/users/${l.account_id}`}
          className="font-medium text-forest-900 hover:underline"
        >
          {l.school_name}
        </Link>
      ),
    },
    {
      key: "licence_key",
      header: "Licence key",
      render: (l) => (
        <code className="rounded bg-cream-100 px-2 py-0.5 font-mono text-xs text-forest-900">
          {l.license_key}
        </code>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (l) => {
        const display =
          l.status === "active"
            ? l.activations_count > 0
              ? "in-use"
              : "available"
            : l.status;
        return <StatusPill tone={statusTones[display] ?? "bark"}>{display}</StatusPill>;
      },
    },
    {
      key: "support",
      header: "Support",
      render: (l) => {
        if (!l.support_ends_at) return <span className="text-forest-400">&mdash;</span>;
        return (
          <span className="text-forest-600">
            until {new Date(l.support_ends_at).toLocaleDateString("en-GB")}
          </span>
        );
      },
    },
    {
      key: "purchased",
      header: "Purchased",
      render: (l) =>
        l.purchased_at ? (
          <span className="text-forest-500">
            {new Date(l.purchased_at).toLocaleDateString("en-GB")}
          </span>
        ) : (
          <span className="text-forest-400">&mdash;</span>
        ),
    },
    {
      key: "created",
      header: "Created",
      render: (l) => (
        <span className="text-forest-500">
          {new Date(l.created_at).toLocaleDateString("en-GB")}
        </span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Licences"
        description="Every licence ever issued. Click a school name to open the owning account."
        actions={
          <>
            <FilterToggle
              label="Show revoked & expired"
              checked={showInactive}
              onChange={setShowInactive}
            />
            <FilterToggle
              label="Show internal users"
              checked={showInternal}
              onChange={setShowInternal}
            />
            <SearchBar
              value={search}
              onChange={setSearch}
              placeholder="Search by licence key or label"
            />
          </>
        }
      />
      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
      <AdminTable
        columns={columns}
        rows={licenses}
        loading={loading}
        emptyMessage={
          debouncedSearch
            ? `No licences match "${debouncedSearch}".`
            : "No licences yet."
        }
      />
      <Pagination
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />
    </div>
  );
}
